<script lang="ts" setup>
  import { computed, ref, useId } from 'vue';
  import { useKeyboardGroupNavigation } from '../_shared/composables/useKeyboardGroupNavigation';
  import TabItem from '../Tabs/TabItem/TabItem.vue';
  import { TabItemVariant } from '../Tabs/TabItem/TabItem.types';
  import type { ButtonGroupItem, ButtonGroupProps } from './ButtonGroup.types';
  import { buttonGroupClass } from './ButtonGroup.css';

  const props = defineProps<ButtonGroupProps>();

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-button-group-${uid}`,
  );

  const emit = defineEmits<{
    'update:modelValue': [value: string];
  }>();

  const groupRef = ref<HTMLElement | null>(null);

  const focusableName = computed(() =>
    props.items.some((item) => item.name === props.modelValue)
      ? props.modelValue
      : props.items[0]?.name,
  );

  const onSelect = (name: string, onClick?: () => void) => {
    if (name === props.modelValue) return;
    emit('update:modelValue', name);
    onClick?.();
  };

  const { onKeydown } = useKeyboardGroupNavigation<ButtonGroupItem>({
    containerRef: groupRef,
    role: 'radio',
    items: computed(() => props.items),
    getValue: (item) => item.name,
    selectedValue: computed(() => props.modelValue),
    onSelect: (name, item) => {
      emit('update:modelValue', name);
      item.onClick?.();
    },
  });
</script>

<template>
  <div
    ref="groupRef"
    :class="buttonGroupClass"
    role="radiogroup"
    :aria-label="ariaLabel"
    :data-testid="resolvedTestId"
    @keydown="onKeydown"
  >
    <TabItem
      v-for="item in items"
      :key="item.name"
      :label="item.label"
      :active="item.name === modelValue"
      :focusable="item.name === focusableName"
      :variant="TabItemVariant.RADIO"
      @select="onSelect(item.name, item.onClick)"
    />
  </div>
</template>
